import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { DistributionApi } from '../../api/distributions/distribution.api';
import { LocalizedEnum } from '../../api/journodex/model/localized-enum';
import { SortOrderEnum } from '../../api/shared/enums/sort-order.enum';
import { TargetingListsApiService } from '../../api/targeting/targeting-lists.api.service';
import { TargetingSearchesApiService } from '../../api/targeting/targeting-searches.api.service';
import { TargetingSearchResponseApiModel } from '../../api/targeting/model/targeting-search-response.api.model';

import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { FilterTypeModel } from '../../Common/Filter/models/abstract-filter-type.model';
import { IdNamePairModel } from '../../Common/Misc/models/id-name-pair.model';
import { JournodexRegionsService } from '../../Common/Journodex/services/journodex-regions.service';
import { JournodexSubjectsService } from '../../Common/Journodex/services/journodex-subjects.service';
import { LocalizationService } from '../../Core/Localization/services/localization.service';
import { OUTLET_SORT_FIELDS } from '../../Common/TargetingShared/model/outlet-sort-fields.const';
import { PageCriteriaModelInterface } from '../../Common/Misc/models/page-criteria.model.interface';
import { SearchModeEnum } from '../../Common/TargetingShared/enum/search-mode.enum';

import { BaseSearchService } from '../../Discover/Targeting/services/base-search.service';
import { JournodexEnumsService } from '../../Discover/Core/services/journodex-enums.service';
import { TargetingResultFactory } from '../../Discover/Targeting/factories/targeting-result.factory';
import { TargetingSearch } from '../../Discover/Targeting/models/targeting-search.model';
import { ViSourceOutlet } from '../models/vi-source-outlet.model';
import { SortFieldEnum } from '../../Common/TargetingShared/enum/SortField.enum';
import { CirculationType } from '../../Discover/Targeting/models/circulation.model';


@Injectable()
export class ViSourceOutletSearchService extends BaseSearchService implements OnDestroy {

    protected outletInfoResults: ViSourceOutlet[] = [];
    protected outletInfoResults$ = new BehaviorSubject<ViSourceOutlet[]>(this.outletInfoResults);

    protected nResults: number = 0;
    protected nResults$ = new BehaviorSubject<number>(this.nResults);

    protected displayResults: ViSourceOutlet[] = [];
    protected displayResults$ = new BehaviorSubject<ViSourceOutlet[]>(this.displayResults);

    protected sortFields$ = new BehaviorSubject<LocalizedEnum[]>([]);

    protected sortField: LocalizedEnum = null;
    protected sortField$ = new BehaviorSubject<LocalizedEnum>(this.sortField);

    protected sortOrder = SortOrderEnum.descending;
    protected sortOrder$ = new BehaviorSubject<SortOrderEnum>(this.sortOrder);

    protected pageCriteria: PageCriteriaModelInterface = { page: 1, pageSize: 25 };
    protected pageCriteria$ = new BehaviorSubject<PageCriteriaModelInterface>(this.pageCriteria);

    public totalNumberOfPages: number = 1;
    protected totalNumberOfPages$ = new BehaviorSubject<number>(this.totalNumberOfPages);

    pagingValidator: () => Observable<boolean>;

    allFilters: FilterTypeModel[];
    page: number = 1;
    pageSize: number = 25;

    readonly sortFields: LocalizedEnum[] = OUTLET_SORT_FIELDS;

    constructor(
        protected targetingSearchesApiService: TargetingSearchesApiService,
        protected targetingListsApiService: TargetingListsApiService,
        protected subjectsService: JournodexSubjectsService,
        protected journodexEnumsService: JournodexEnumsService,
        protected journodexRegionService: JournodexRegionsService,
        protected localizationService: LocalizationService,
        protected targetingResultFactory: TargetingResultFactory,
        protected currentUserStateService: CurrentUserStateService,
        protected distributionApiService: DistributionApi,
    ) {
        super(
            targetingSearchesApiService,
            targetingListsApiService,
            subjectsService,
            journodexEnumsService,
            journodexRegionService,
            localizationService,
            targetingResultFactory,
            currentUserStateService,
            distributionApiService
        );

        this.sortFields$.next(this.sortFields);
        this.sortField = this.sortFields[0];
        this.sortField$.next(this.sortField);
    }


    getFilterDefinitions(): FilterTypeModel[] {
        return [
            this.outletNameFilter,
            this.subjectFilter,
            this.languageFilter,
            this.targetAudienceRegionFilter,
            this.coverageFocusFilter,
            this.audienceReachFilter,
            this.marketSectorFilter,
            this.mediaTypeFilter,
            this.productTypeFilter,
            this.productFrequencyFilter,
            this.targetAudienceFilter,
            this.listFilter
        ];
    }

    setResults(results: ViSourceOutlet[]) {
        this.outletInfoResults = results ?? [];
        this.page = 1;
        this.pageCriteria = { page: 1, pageSize: this.pageSize };
        this.pageCriteria$.next(this.pageCriteria);
        this.doSearch(true, false);
    }


    setSortField(field: any) {
        if (field !== this.sortField) {
            this.sortField = field;
            this.sortField$.next(field);
            this.doSearch(true, false);
        }
    }

    setSortOrder(order: SortOrderEnum) {
        if (this.sortOrder !== order) {
            this.sortOrder = order;
            this.sortOrder$.next(order);
            this.doSearch(true, false);
        }
    }

    doSearch(
        refreshPrimaryFacets: boolean = false,
        refreshFilteredFacets: boolean = false
    ): Observable<TargetingSearchResponseApiModel> {

        if (!this.hasFiltersToApply() || !this.outletInfoResults.length) {
            this.renderLocalResults();
            if (refreshPrimaryFacets && this.outletInfoResults.length) {
                this.refreshPrimaryFacets(this.buildIdSearch());
            }
            return of(null);
        }

        return this.doBackendSearch(refreshPrimaryFacets, refreshFilteredFacets);
    }


    private doBackendSearch(
        refreshPrimaryFacets: boolean,
        refreshFilteredFacets: boolean
    ): Observable<TargetingSearchResponseApiModel> {

        if (this.mainSearchRequest) {
            this.mainSearchRequest.unsubscribe();
            this.mainSearchRequest = null;
        }

        const filters = this.generateFiltersSearch();
        const search = this.buildIdSearch();
        const mainSearchRequest = this.fastSearch(search, filters);

        this.searchLoading$.next(true);
        this.mainSearchRequest = mainSearchRequest.subscribe({
            next: () => {
                this.mainSearchRequest = null;
                this.searchLoading$.next(false);
            },
            error: (err) => console.log(err)
        });

        if (refreshFilteredFacets) {
            this.filteredFacetRequest?.unsubscribe();
            this.filteredFacetRequest = this.facetSearch(search, filters).subscribe(collection => {
                this.setFilteredFacets(collection);
                this.filteredFacetRequest = null;
            });
        }

        if (refreshPrimaryFacets) {
            this.refreshPrimaryFacets(search);
        }

        return mainSearchRequest;
    }



    async setSearchResults(response: TargetingSearchResponseApiModel): Promise<void> {
        super.setSearchResults(response);
        const responseOutletIds = response.results.map(x => x.outletInfo.id);

        this.displayResults = responseOutletIds.map(id => {
            return this.outletInfoResults.find(x => x.outlet.id === id);
        }).filter(x => !!x);
        this.displayResults$.next(this.displayResults);
    }

    getSortableFields(): Observable<any[]> {
        return this.sortFields$;
    }

    getSortField(): Observable<any> {
        return this.sortField$;
    }

    getSortOrder(): Observable<SortOrderEnum> {
        return this.sortOrder$;
    }

    getItemCount(): Observable<number> {
        return this.nResults$;
    }

    getDisplayResults(): Observable<ViSourceOutlet[]> {
        return this.displayResults$;
    }

    getPageCriteria(): Observable<PageCriteriaModelInterface> {
        return this.pageCriteria$;
    }

    setMaxPageCriteria(totalNumberOfPages: number): void {
        this.totalNumberOfPages = totalNumberOfPages;
        this.totalNumberOfPages$.next(this.totalNumberOfPages);
    }

    getMaxPageCriteria(): Observable<number> {
        return this.totalNumberOfPages$;
    }

    setPageCriteria(pageCriteria: PageCriteriaModelInterface): void {
        this.page = pageCriteria.page;
        this.pageSize = pageCriteria.pageSize;
        this.nResults$.next(this.nResults);
        if (this.page <= this.totalNumberOfPages) {
            this.pageCriteria = pageCriteria;
            this.pageCriteria$.next(pageCriteria);
            this.doSearch(true, false);
        }
    }

    canSetPageCriteria(): Observable<boolean> {
        if (this.pagingValidator) {
            return this.pagingValidator();
        }
        return of(true);
    }

    private refreshPrimaryFacets(search: TargetingSearch) {
        this.primaryFacetRequest?.unsubscribe();
        this.primaryFacetRequest = this.facetSearch(search).subscribe(collection => {
            this.setPrimaryFacets(collection);
            this.primaryFacetRequest = null;
        });
    }

    private renderLocalResults() {
        this.mainSearchRequest?.unsubscribe();
        this.mainSearchRequest = null;

        const sorted = this.sortLocal(this.outletInfoResults);
        const total = sorted.length;

        this.nResults = total;
        this.nResults$.next(total);
        this.setMaxPageCriteria(Math.max(1, Math.ceil(total / this.pageSize)));

        const start = (this.page - 1) * this.pageSize;
        this.displayResults = sorted.slice(start, start + this.pageSize);
        this.displayResults$.next(this.displayResults);
        this.searchLoading$.next(false);
    }

    private sortLocal(results: ViSourceOutlet[]): ViSourceOutlet[] {
        const direction = this.sortOrder === SortOrderEnum.ascending ? 1 : -1;
        return results
            .map((item, index) => ({ item, index, value: this.getSortValue(item) }))
            .sort((a, b) => {
                const aEmpty = a.value == null || a.value === '';
                const bEmpty = b.value == null || b.value === '';
                // Missing values always go last; ties keep your original order
                if (aEmpty && bEmpty) {
                    return a.index - b.index;
                }
                if (aEmpty) {
                    return 1;
                }
                if (bEmpty) {
                    return -1;
                }

                const compared = typeof a.value === 'string' && typeof b.value === 'string'
                    ? a.value.localeCompare(b.value, undefined, { sensitivity: 'base' })
                    : (a.value as number) - (b.value as number);

                return compared * direction || a.index - b.index;
            })
            .map(x => x.item);
    }

    private getSortValue(info: ViSourceOutlet): number | string | undefined {
        const outlet = info.outlet;
        switch (this.sortField?.enumValue) {
            case SortFieldEnum.OUTLET_NAME:
                return outlet.name;
            case SortFieldEnum.COUNTRY_NAME:
                return outlet.geography?.country?.name;
            case SortFieldEnum.PROVINCE_NAME:
                return outlet.geography?.province?.name;
            case SortFieldEnum.CITY_NAME:
                return outlet.geography?.city?.name;
            default:
                return this.getReachValue(info);
        }
    }

    getReachValue(info: ViSourceOutlet): number | undefined {
        const circulations = info.outlet.circulations;
        if (!circulations) {
            return undefined;
        }
        const byType = (type: CirculationType) =>
            circulations.find(x => x.circulationType.enumValue === type)?.value;

        switch (this.sortField?.enumValue) {
            case SortFieldEnum.CIRCULATION_CIRCULATION:
                return byType(CirculationType.circulation);
            case SortFieldEnum.CIRCULATION_FOLLOWERS:
                return byType(CirculationType.followers);
            case SortFieldEnum.CIRCULATION_LISTENERS:
                return byType(CirculationType.listeners);
            case SortFieldEnum.CIRCULATION_VIEWERS:
                return byType(CirculationType.viewers);
            default:
                return circulations.find(x =>
                    x.circulationType.enumValue === CirculationType.manualValue ||
                    x.circulationType.enumValue === CirculationType.similarWebValue
                )?.value;
        }
    }

    private buildIdSearch(): TargetingSearch {
        const search = new TargetingSearch();
        search.outletIdsNames.include = this.outletInfoResults
            .map(x => x.outlet.id)
            .filter(x => !!x)
            .map(x => new IdNamePairModel(null, x));
        search.searchMode = SearchModeEnum.outlet;

        return search;
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.mainSearchRequest?.unsubscribe();
        this.filteredFacetRequest?.unsubscribe();
        this.primaryFacetRequest?.unsubscribe();
    }
}
